// Comment mode and the page's pins (apps/pipeline_api/src/feedback.rs). Mounted once in the shell; does
// nothing unless Preview is on.
//
// In comment mode, hovering (or focusing, for keyboard users) highlights an element; clicking it (or
// Enter) opens the composer anchored to it. Clicks never reach the page while comment mode is on. Esc
// closes the composer, then the thread, then the list, then comment mode. Pins of this page show as
// numbered markers; those whose element isn't on screen are in the side list.
import { MessageSquarePlus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useSearchParams } from "react-router";
import { toast } from "sonner";
import { ApiError } from "../../lib/api";
import { BUILD_SHA } from "../../lib/build";
import { buildAnchor, FEEDBACK_UI_ATTR, isFeedbackUi, type Anchor } from "../../lib/feedback/anchor";
import { pageFilter, useFeedbackActions, useInfiniteFeedbackList, type NewPin } from "../../lib/feedback/api";
import { captureArea } from "../../lib/feedback/capture";
import { FEEDBACK_PARAM, pageContext, routeListsLoans } from "../../lib/feedback/context";
import { LOAN_LIST_ATTR, selectedSubject } from "../../lib/feedback/subject";
import { commentMode, composerPosition, toggleCommentMode } from "../../lib/feedback/mode";
import { placePins } from "../../lib/feedback/place";
import { DICTATION_ENABLED, speechCtor } from "../../lib/feedback/speech";
import { usePreview } from "../../lib/preview";
import { cx } from "../ui";
import { FeedbackComposer } from "./FeedbackComposer";
import { excerpt, PinList } from "./PinList";
import { PinThreadPanel } from "./PinThread";

const PAGE_PIN_CAP = 2_000;
const PAGE_REQUEST_CAP = 10;

const UI = { [FEEDBACK_UI_ATTR]: "" };

/** The top bar's toggle. Part of the feedback UI, so in comment mode it switches the mode off. */
export function FeedbackButton() {
  const p = usePreview();
  const mode = commentMode.use();
  const { pathname, search } = useLocation();
  const q = useInfiniteFeedbackList(pageFilter(pageContext(pathname, search)), p.on);
  if (!p.on) return null;
  const loaded = q.data?.pages.flatMap((page) => page.pins).length ?? 0;
  const n = Math.min(loaded, PAGE_PIN_CAP);
  const more = loaded > PAGE_PIN_CAP || q.hasNextPage;
  return (
    <button
      type="button"
      {...UI}
      onClick={toggleCommentMode}
      aria-pressed={mode}
      aria-label={n ? `Feedback, ${n}${more ? "+" : ""} pin${n === 1 ? "" : "s"} on this page` : "Feedback"}
      title={mode ? "Leave comment mode (Esc)" : "Leave feedback on this page"}
      className={cx(
        "focus-ring relative inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-md px-2 text-[13px] font-medium",
        mode ? "bg-white text-navy-900" : "text-white/85 hover:bg-white/10 hover:text-white",
      )}
    >
      <MessageSquarePlus size={18} aria-hidden />
      <span className="max-lg:sr-only">Feedback</span>
      {n ? (
        <span aria-hidden className="absolute top-1 right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-green-500 px-1 text-[10px] font-bold text-navy-950 lg:static">
          {n}{more ? "+" : ""}
        </span>
      ) : null}
    </button>
  );
}

export function FeedbackLayer() {
  const p = usePreview();
  return p.on ? <FeedbackLayerOn /> : null;
}

interface Target {
  el: Element;
  click?: { clientX: number; clientY: number };
  anchor: Anchor;
  subject: ReturnType<typeof selectedSubject>;
}

function FeedbackLayerOn() {
  const mode = commentMode.use();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const deepLink = params.get(FEEDBACK_PARAM);
  const ctx = useMemo(() => pageContext(location.pathname, location.search), [location.pathname, location.search]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const actions = useFeedbackActions();
  const speech = useMemo(() => (DICTATION_ENABLED ? speechCtor() : null), []);
  const pinsQ = useInfiniteFeedbackList(pageFilter(ctx), true);
  const pins = useMemo(() => pinsQ.data?.pages.flatMap((page) => page.pins).slice(0, PAGE_PIN_CAP) ?? [], [pinsQ.data]);
  const { hasNextPage, isFetching, isError, fetchNextPage } = pinsQ;
  const loaded = pinsQ.data?.pages.reduce((n, page) => n + page.pins.length, 0) ?? 0;
  const pageCount = pinsQ.data?.pages.length ?? 0;
  const capped = loaded > PAGE_PIN_CAP || !!hasNextPage && (loaded >= PAGE_PIN_CAP || pageCount >= PAGE_REQUEST_CAP);
  // Older pages load only while comment mode is open; otherwise the first page feeds the top-bar count.
  useEffect(() => {
    if (mode && hasNextPage && !isFetching && !isError && pins.length < PAGE_PIN_CAP && pageCount < PAGE_REQUEST_CAP) void fetchNextPage();
  }, [mode, hasNextPage, isFetching, isError, fetchNextPage, pins.length, pageCount]);
  const listNotice = capped ? "Automatic loading limit reached on this view. Use All feedback to see more."
    : isError ? "Could not load every pin. Reopen this page to retry, or use All feedback."
    : isFetching ? "Loading pins…" : undefined;

  // A deep link (`?feedback=<id>`, from the All feedback panel) opens comment mode on that pin.
  useEffect(() => {
    if (!deepLink) return;
    commentMode.set(true);
    setActiveId(deepLink);
  }, [deepLink]);

  // A new page drops the half-written target.
  useEffect(() => {
    setTarget(null);
    setHover(null);
  }, [location.pathname, location.search]);

  const closeThread = () => {
    setActiveId(null);
    if (params.has(FEEDBACK_PARAM)) {
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete(FEEDBACK_PARAM);
          return n;
        },
        { replace: true },
      );
    }
  };
  // Leaving comment mode any way (the top-bar button too, not just exit()) drops every overlay and
  // the deep link, so reopening starts clean.
  useEffect(() => {
    if (mode) return;
    setTarget(null);
    setHover(null);
    setListOpen(false);
    setActiveId(null);
    if (deepLink) {
      setParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete(FEEDBACK_PARAM);
          return n;
        },
        { replace: true },
      );
    }
  }, [mode, deepLink, setParams]);

  const exit = () => {
    commentMode.set(false);
    setTarget(null);
    setHover(null);
    setListOpen(false);
    closeThread();
  };
  const escape = () => {
    if (target) setTarget(null);
    else if (activeId) closeThread();
    else if (listOpen) setListOpen(false);
    else exit();
  };
  const select = (el: Element, click?: { clientX: number; clientY: number }) => {
    setHover(null);
    setError(null);
    setActiveId(null);
    try {
      const subject = selectedSubject(el, ctx, click);
      // Keyboard grid cells live in Glide's accessibility DOM. Store the canvas as the anchor
      // for both input paths; the subject lookup supplies the actual row rectangle.
      const anchored = subject.gridHit ? el.closest("[data-feedback-grid]")?.querySelector("canvas") ?? el : el;
      const anchor = buildAnchor(anchored, click);
      const { loan_id, core_loan_id } = subject.context;
      anchor.subject = { loan_id, core_loan_id };
      if (subject.loanList) anchor.data[LOAN_LIST_ATTR] = "true";
      if (subject.gridHit) {
        anchor.text = null; // Canvas accessibility text can contain every row.
        const r = subject.gridHit.rect;
        anchor.rect = { x: r.x + window.scrollX, y: r.y + window.scrollY, width: r.width, height: r.height };
        anchor.point = click && r.width > 0 && r.height > 0
          ? { x: Math.max(0, Math.min(1, (click.clientX - r.x) / r.width)), y: Math.max(0, Math.min(1, (click.clientY - r.y) / r.height)) }
          : { x: 0.5, y: 0.5 };
      }
      setTarget({ el, click, anchor, subject });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Open the loan before pinning this area.");
    }
  };

  // Handlers read the latest closures through a ref, so the listeners are bound once per mode.
  const latest = useRef({ escape, select, composing: false });
  useEffect(() => {
    latest.current = { escape, select, composing: !!target };
  });

  // Keep the feedback UI usable over dialogs: their focus traps and outside-click handlers listen on
  // the document, so events that start in the feedback UI stop at the window.
  useEffect(() => {
    const guard = (e: Event) => {
      const t = e.target as Element | null;
      const related = (e as FocusEvent).relatedTarget as Element | null;
      if (isFeedbackUi(t) || (e.type === "focusout" && isFeedbackUi(related))) e.stopPropagation();
    };
    const types = ["focusin", "focusout", "pointerdown", "mousedown", "wheel", "touchmove"];
    types.forEach((t) => window.addEventListener(t, guard, true));
    return () => types.forEach((t) => window.removeEventListener(t, guard, true));
  }, []);

  useEffect(() => {
    if (!mode) return;
    const root = document.documentElement;
    root.dataset.commentMode = "";
    let raf = 0;
    const highlight = (el: Element | null) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setHover(el && el !== document.body && el !== root && !isFeedbackUi(el) ? el.getBoundingClientRect() : null));
    };
    const onMove = (e: PointerEvent) => {
      if (!latest.current.composing) highlight(e.target as Element);
    };
    const block = (e: Event) => {
      if (isFeedbackUi(e.target as Element)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Element;
      if (isFeedbackUi(t)) return;
      e.preventDefault();
      e.stopPropagation();
      if (t instanceof Element && t !== document.body && t !== root) latest.current.select(t, { clientX: e.clientX, clientY: e.clientY });
    };
    const onFocus = (e: FocusEvent) => {
      if (!latest.current.composing) highlight(e.target as Element);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        latest.current.escape();
        return;
      }
      if (e.key === "Enter" && !latest.current.composing) {
        const t = document.activeElement;
        if (t && t !== document.body && !isFeedbackUi(t)) {
          e.preventDefault();
          e.stopPropagation();
          latest.current.select(t);
        }
      }
    };
    const bump = () => setTick((n) => n + 1);
    window.addEventListener("pointermove", onMove, true);
    for (const t of ["pointerdown", "mousedown", "pointerup", "mouseup", "dblclick", "contextmenu"]) window.addEventListener(t, block, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("focusin", onFocus, true);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", bump, true);
    window.addEventListener("resize", bump);
    const iv = window.setInterval(bump, 1000);
    return () => {
      delete root.dataset.commentMode;
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove, true);
      for (const t of ["pointerdown", "mousedown", "pointerup", "mouseup", "dblclick", "contextmenu"]) window.removeEventListener(t, block, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("focusin", onFocus, true);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", bump, true);
      window.removeEventListener("resize", bump);
      window.clearInterval(iv);
      setHover(null);
    };
  }, [mode]);

  if (!mode) return null;

  const placement = placePins(pins, ctx, document, window);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const targetRect = target?.el.isConnected ? target.el.getBoundingClientRect() : null;
  const box = targetRect ?? hover;

  const save = async (note: string) => {
    if (!target) return;
    setSaving(true);
    setError(null);
    // A refreshed/recycled row must not change subject while the composer is open.
    const subject = target.subject;
    const stillSelected = () => {
      if (!target.el.isConnected) return false;
      try {
        const current = selectedSubject(target.el, ctx, target.click);
        return JSON.stringify(current.context) === JSON.stringify(subject.context)
          && current.loanList === subject.loanList;
      } catch { return false; }
    };
    if (!stillSelected()) {
      setError("The selected content changed. Select it again.");
      setSaving(false);
      return;
    }
    const shot = subject.gridHit ? null : await captureArea(target.el).catch(() => null);
    if (!stillSelected()) {
      setError("The selected content changed. Select it again.");
      setSaving(false);
      return;
    }
    const body: NewPin = {
      note,
      route: ctx.route,
      query: ctx.query,
      loan_id: subject.context.loan_id,
      core_loan_id: subject.context.core_loan_id,
      lo_person_id: subject.context.lo_person_id,
      anchor: target.anchor,
      viewport: { width: Math.max(1, Math.round(vw)), height: Math.max(1, Math.round(vh)) },
      build_sha: BUILD_SHA,
      screenshot: shot,
    };
    try {
      let d;
      try {
        d = await actions.create(body);
      } catch (e) {
        // The screenshot alone was refused: keep the note.
        if (e instanceof ApiError && (e.code === "screenshot_too_large" || e.field === "screenshot") && body.screenshot) {
          d = await actions.create({ ...body, screenshot: null });
          toast.message("Saved without a screenshot");
        } else throw e;
      }
      toast.success(`Pin ${d.number} saved`);
      setTarget(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save the pin");
    } finally {
      setSaving(false);
    }
  };

  const selected = target?.subject.context ?? ctx;
  const scopeNote = selected.loan_id || selected.core_loan_id ? "Loan in view: only people who can see this loan will see this pin." : selected.lo_person_id ? "LO in view: only prospecting staff will see this pin." : routeListsLoans(ctx.route) || target?.subject.loanList ? "This area may show several loans: only you and staff with access to all loans will see this pin." : "Only you and staff with access to all loans will see this pin.";

  return createPortal(
    <div {...UI} className="feedback-layer">
      {box ? (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[76] rounded-[3px] border-2 border-focus bg-[color-mix(in_srgb,var(--focus)_6%,transparent)]"
          style={{ left: box.left - 2, top: box.top - 2, width: box.width + 4, height: box.height + 4 }}
        />
      ) : null}
      {placement.placed
        .filter((p) => p.x >= 0 && p.y >= 0 && p.x <= vw && p.y <= vh)
        .map(({ pin, x, y, approximate }) => (
          <button
            key={pin.id}
            type="button"
            onClick={() => {
              setTarget(null);
              setActiveId(pin.id);
            }}
            aria-label={`Pin ${pin.number}: ${excerpt(pin.note, 60)}`}
            title={approximate ? "Approximate position: the element has changed" : excerpt(pin.note, 120)}
            className="focus-ring pointer-events-auto fixed z-[77] flex h-11 w-11 items-center justify-center rounded-full"
            style={{ left: Math.min(vw - 44, Math.max(0, x - 22)), top: Math.min(vh - 44, Math.max(0, y - 22)) }}
          >
            <span
              className={cx(
                "flex h-7 min-w-7 items-center justify-center rounded-full rounded-bl-none px-1 text-[12px] font-bold shadow-[var(--shadow-2)] ring-2 ring-white",
                pin.id === activeId ? "bg-accent text-on-accent" : "bg-primary text-on-primary",
                approximate && "opacity-75",
              )}
            >
              {pin.number}
            </span>
          </button>
        ))}
      {!target ? (
        <div
          role="toolbar"
          aria-label="Comment mode"
          className="pointer-events-auto fixed bottom-3 left-1/2 z-[78] flex max-w-[calc(100vw-24px)] -translate-x-1/2 items-center gap-1 rounded-full bg-navy-900 px-2 text-white shadow-[var(--shadow-3)] dark:border dark:border-line"
        >
          <span className="px-2 text-[13px] whitespace-nowrap max-sm:hidden">Click anything to leave feedback · Esc to exit</span>
          {placement.unplaced.length || listNotice ? (
            <button type="button" onClick={() => setListOpen((o) => !o)} aria-expanded={listOpen} className="focus-ring h-11 rounded-full px-3 text-[13px] font-medium whitespace-nowrap hover:bg-white/10">
              {placement.unplaced.length} not on screen
            </button>
          ) : null}
          <Link to="/feedback" onClick={exit} className="focus-ring inline-flex h-11 items-center rounded-full px-3 text-[13px] font-medium whitespace-nowrap hover:bg-white/10">
            All feedback
          </Link>
          <button type="button" onClick={exit} className="focus-ring h-11 rounded-full bg-white/12 px-4 text-[13px] font-semibold hover:bg-white/20">
            Done
          </button>
        </div>
      ) : null}
      {listOpen ? <PinList pins={placement.unplaced} activeId={activeId} onOpen={(id) => setActiveId(id)} onClose={() => setListOpen(false)} notice={listNotice} /> : null}
      {target && targetRect ? (
        <FeedbackComposer
          key={JSON.stringify(target.anchor)}
          position={composerPosition(targetRect, { width: vw, height: vh })}
          saving={saving}
          error={error}
          speech={speech}
          scopeNote={scopeNote}
          onSave={(note) => void save(note)}
          onCancel={() => setTarget(null)}
        />
      ) : null}
      {activeId ? <PinThreadPanel id={activeId} onClose={closeThread} /> : null}
    </div>,
    document.body,
  );
}
